import { DependencyList, useEffect } from 'react';

import { isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doGetOrderList } from 'src/actions';
import { GetOrderListQueryParamsDto } from 'src/dtos';
import { Order } from 'src/models';

import { CustomHookBaseOptions } from '../models/common.model';

import { useAccountSelector } from './useAccountSelector';
import { useOrderListSelector } from './useOrderListSelector';

interface UseOrdersReturnType {
  orderList: Order[];
  isOrderListLoading: boolean;
  hasOrderListError?: boolean;
  orderListError?: string;
  orderListTotal: number;
}

export interface UseOrdersOptions extends CustomHookBaseOptions {
  refetchOnChange?: boolean;
  queryParamsDto?: GetOrderListQueryParamsDto;
  dependency?: DependencyList;
}

export const useOrders = (
  { skip, refetchOnChange, queryParamsDto, dependency = [] }: UseOrdersOptions = { skip: false },
): UseOrdersReturnType => {
  const dispatch = useDispatch();
  const orderListRaw: Order[] | undefined = useSelector((state: any) => state.orders.list?.data?.data);
  const { orderList, isOrderListLoading, hasOrderListError, orderListError } = useOrderListSelector();
  const payload = useSelector((state: any) => state.orders.list?.payload);
  const orderListTotal: number = useSelector((state: any) => state.orders.list?.data?.total ?? 0);

  const { account } = useAccountSelector();

  const fetchList = () => {
    if (!account?.accountId) {
      return;
    }

    dispatch(doGetOrderList({ params: { accountId: account.accountId }, queryParams: queryParamsDto }));
  };

  useEffect(() => {
    if (skip) {
      return;
    }

    if (!orderListRaw && !isOrderListLoading && !hasOrderListError) {
      fetchList();

      return;
    }

    if (
      refetchOnChange ||
      !isEqual(payload, { params: { accountId: account?.accountId }, queryParams: queryParamsDto })
    ) {
      fetchList();
    }
  }, dependency);

  return {
    orderList,
    isOrderListLoading,
    hasOrderListError,
    orderListError,
    orderListTotal,
  };
};
